/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { 
  Download, 
  ArrowRight, 
  Shield, 
  Activity, 
  Globe, 
  Mail, 
  ExternalLink, 
  RefreshCw, 
  Lock, 
  ShieldAlert,
  Menu,
  X,
  Check,
  Bookmark,
  Layers,
  ChevronDown,
  ArrowUpRight,
  Sprout,
  FlaskConical,
  Truck,
  Sparkles,
  Search,
  SlidersHorizontal,
  ChevronRight,
  Quote,
  Award,
  MapPin,
  Building2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

const LOGO_URL = "/logo.png";
const HERO_IMG = "/hero.jpg";
const GRANULAR_IMG = "/granular.jpg";
const MICRONIZED_IMG = "/micronized.jpg";
const PHOSPHATE_ROCK_IMG = "/phosphate-rock.jpg";
const NPK_IMG = "/npk.jpg";
const AMMONIUM_SULFATE_IMG = "/ammonium-sulfate.jpg";
const UREA_IMG = "/urea.jpg";
const SSP_IMG = "/ssp.jpg";
const TSP_IMG = "/tsp.jpg";

const EDITION_YEAR = "2026 Edition";

// Dedicated clean header for A4 Export pages ONLY
function PDF_PageHeader({ sectionTitle = "Product & Supply Overview", category = "Institutional Profile" }: { sectionTitle?: string; category?: string }) {
  return (
    <header className="px-12 py-4 h-[72px] flex justify-between items-center border-b border-slate-200 bg-white shrink-0">
      <div className="flex items-center gap-3">
        <div className="bg-white p-1 rounded flex items-center justify-center border border-slate-200/80 h-10 w-24">
          <img 
            src={LOGO_URL} 
            className="h-full w-auto object-contain mix-blend-multiply brightness-125 contrast-125" 
            alt="Logo" 
          />
        </div>
        <div>
          <span className="block font-display font-bold text-xs text-deep-navy uppercase tracking-wider">Awtad Trade</span>
          <span className="block text-[8px] font-technical uppercase tracking-widest text-slate-400">Global Commodities &bull; Cairo, Egypt</span>
        </div>
      </div>
      <div className="text-right">
        <span className="text-[8px] font-technical uppercase tracking-widest text-industrial-blue font-semibold block">{category}</span>
        <span className="text-sm font-display font-bold text-deep-navy">{sectionTitle}</span>
        <span className="text-[8px] font-technical text-slate-400 block">{EDITION_YEAR}</span>
      </div>
    </header>
  );
}

// Dedicated clean footer for A4 Export pages ONLY
function PDF_PageFooter({ pageNumber, totalPages = 8, title = "Corporate Profile" }: { pageNumber: number; totalPages?: number; title?: string }) {
  return (
    <footer className="px-12 py-3 h-[42px] flex justify-between items-center border-t border-slate-200 bg-white text-[8px] font-technical uppercase tracking-wider text-slate-400 shrink-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-deep-navy">Awtad Trade</span>
        <span>&bull;</span>
        <span>4 Makram Ebeid Street, Nasr City - Cairo - Egypt</span>
      </div>
      <div className="flex items-center gap-3">
        <span>{title}</span>
        <span>&bull;</span>
        <span className="font-semibold text-slate-700">Page {pageNumber} of {totalPages}</span>
      </div>
    </footer>
  );
}

const getApiUrl = (endpoint: string) => {
  // If we are in local development or preview containers:
  if (window.location.hostname === "localhost" || window.location.hostname.includes("run.app")) {
    return endpoint;
  }
  
  // For production hosting (like cPanel Selector):
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  
  if (pathParts.length > 0 && pathParts[pathParts.length - 1].includes('.')) {
    pathParts.pop();
  }
  
  const baseSubfolder = pathParts.length > 0 ? `/${pathParts.join('/')}/` : '/';
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  
  return `${baseSubfolder}${cleanEndpoint}`;
};

let cachedApiPathPrefix: string | null = null;

const buildResilientUrl = (prefix: string, endpoint: string) => {
  let finalPrefix = prefix;
  let finalEndpoint = endpoint;

  const hasProtocol = finalPrefix.startsWith("http://") || finalPrefix.startsWith("https://");
  let protocol = "";
  if (hasProtocol) {
    const idx = finalPrefix.indexOf("://");
    protocol = finalPrefix.substring(0, idx + 3);
    finalPrefix = finalPrefix.substring(idx + 3);
  }

  finalPrefix = finalPrefix.replace(/\/+/g, "/");
  if (finalPrefix.endsWith("/")) {
    finalPrefix = finalPrefix.slice(0, -1);
  }
  if (finalEndpoint.startsWith("/")) {
    finalEndpoint = finalEndpoint.substring(1);
  }

  const joinedPath = `${finalPrefix}/${finalEndpoint}`.replace(/\/+/g, "/");
  const baseParts = `${protocol}${joinedPath}`;
  
  const sep = baseParts.includes("?") ? "&" : "?";
  return `${baseParts}${sep}api=${endpoint}`;
};

const resilientFetch = async (baseEndpoint: string, options?: RequestInit) => {
  if (cachedApiPathPrefix !== null) {
    try {
      const url = buildResilientUrl(cachedApiPathPrefix, baseEndpoint);
      const response = await fetch(url, options);
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        return { ok: response.ok, status: response.status, data };
      }
    } catch (e) {
      console.warn("Cached API Path Prefix no longer active. Proceeding to rediscovery...", e);
      cachedApiPathPrefix = null;
    }
  }

  const dynamicRoot = getApiUrl("");
  const prefixes = [
    window.location.origin + window.location.pathname,
    window.location.pathname,
    dynamicRoot,
    "/server.js",
    "/server.js/api",
    "/api",
    "",
    "api"
  ].filter((value, index, self) => self.indexOf(value) === index);

  let lastStatus = 404;

  for (const prefix of prefixes) {
    const url = buildResilientUrl(prefix, baseEndpoint);
    
    try {
      console.log(`[API DISCOVERY] Probing route: ${url}`);
      const response = await fetch(url, options);
      const contentType = response.headers.get("content-type");
      
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        console.log(`[API DISCOVERY SUCCESS] Established communication on route: ${url}`);
        cachedApiPathPrefix = prefix;
        return { ok: response.ok, status: response.status, data };
      }
      lastStatus = response.status;
    } catch (err: any) {
      console.warn(`[API DISCOVERY PROBE FAILED] Route: ${url}`, err.message);
    }
  }

  throw { status: lastStatus, message: "Unable to establish communication with the Node.js Express server on any verified API paths." };
};

// Anti-Spam Harvesting Secure Email Component
const SecureEmail = ({ className = "", strong = false }: { className?: string, strong?: boolean }) => {
  const user = ["i", "n", "f", "o"].join("");
  const domain = ["a", "w", "t", "a", "d", "t", "r", "a", "d", "e", ".", "c", "o", "m"].join("");
  const fullEmail = `${user}@${domain}`;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = `mailto:${fullEmail}`;
  };

  if (strong) {
    return (
      <strong 
        onClick={handleClick}
        className={`cursor-pointer hover:underline text-sulfur-gold select-all inline-flex items-center ${className}`}
        title="Protected Corporate Email"
      >
        <span>{user}</span>
        <span style={{ display: 'none' }}>antispam-bot</span>
        <span>@</span>
        <span>{domain}</span>
      </strong>
    );
  }

  return (
    <a 
      href="#" 
      onClick={handleClick}
      className={`cursor-pointer hover:underline inline-flex items-center ${className}`}
      title="Protected Corporate Email"
    >
      <span>{user}</span>
      <span style={{ display: 'none' }}>antispam-bot</span>
      <span>@</span>
      <span>{domain}</span>
    </a>
  );
};

// Complete Product Definition Schema for all 7 Commodities
export interface ProductItem {
  id: string;
  name: string;
  shortName: string;
  badge: string;
  category: "Sulfur" | "Phosphates" | "Nitrogen" | "Compound";
  image: string;
  subtitle: string;
  agronomicUse: string;
  production: string;
  chemicalFormula?: string;
  specs: { label: string; value: string }[];
  keyHighlights: string[];
}

export const PRODUCTS_LIST: ProductItem[] = [
  {
    id: "sulfur",
    name: "Industrial Sulfur (Granular & Micronized)",
    shortName: "Sulfur",
    badge: "Purity ≥99.9%",
    category: "Sulfur",
    image: GRANULAR_IMG,
    subtitle: "High-Purity Elemental Sulfur for Agriculture, Sulfuric Acid, and Polymers",
    agronomicUse: "Crucial secondary macronutrient essential for nitrogen fixation, amino acid synthesis (cysteine, methionine), and chlorophyll formation in crops. Extensively used in agricultural soils to correct alkalinity and manufactured into sulfur-bentonite fertilizers.",
    production: "Predominantly recovered from natural gas sweetening and hydro-desulfurization of crude oil at world-scale refineries. The molten sulfur is treated, degassed, and formed into spherical solid granules (2.0-5.0mm) via air/water fluid bed granulation or finely milled into micronized powder (100-325 mesh) for specialized fungicide and vulcanization uses.",
    chemicalFormula: "S (Elemental Sulfur, CAS #7704-34-9)",
    specs: [
      { label: "PURITY", value: "99.9% Minimum" },
      { label: "FORMS", value: "Granular (2-5mm) & Micronized Powder" },
      { label: "ASH CONTENT", value: "0.03% Maximum" },
      { label: "STANDARDS", value: "ASTM Certified & Dust Suppressed" }
    ],
    keyHighlights: [
      "Low moisture (<0.5%) & friction-resistant granules",
      "Essential precursor for high-density sulfuric acid",
      "Supplied in bulk vessels, containerized jumbo bags, or bagged"
    ]
  },
  {
    id: "phosphate-rock",
    name: "Phosphate Rock (PR)",
    shortName: "Phosphate Rock",
    badge: "28%–34% P₂O₅",
    category: "Phosphates",
    image: PHOSPHATE_ROCK_IMG,
    subtitle: "Natural Sedimentary Apatite Ore for Fertilizer Synthesis & Direct Application",
    agronomicUse: "Phosphorus additions are needed globally to improve soil fertility and crop production. Unprocessed phosphate rock (PR) applied directly to soil can provide a valuable, sustained plant nutrient source under specific conditions—especially in acidic soils, agroforestry, and organic farming systems.",
    production: "Phosphate rock is obtained from geologic deposits worldwide (primarily apatite, a calcium phosphate mineral). It is extracted mainly from sedimentary marine deposits through surface mining, with smaller amounts from underground mines or igneous sources. Impurities are screened near the mine site. While mostly used to manufacture soluble P fertilizers, highly reactive PR sources can be applied directly to soil, lowering nutrient costs and suiting organic farming systems.",
    chemicalFormula: "Ca₁₀(PO₄)₆(F,Cl,OH)₂ [Apatite Mineral]",
    specs: [
      { label: "P₂O₅ CONTENT", value: "28.0% &mdash; 34.0% Grade" },
      { label: "SOURCE", value: "Sedimentary Marine Deposit" },
      { label: "REACTIVITY", value: "Direct Application & Acidulation Grades" },
      { label: "MOISTURE", value: "≤ 3.0% Max Controlled" }
    ],
    keyHighlights: [
      "Naturally rich in micronutrients and calcium",
      "Ideal for direct soil application or downstream phosphoric acid / SSP / TSP manufacture",
      "High bulk density suitable for ocean vessel chartered logistics"
    ]
  },
  {
    id: "npk",
    name: "NPK Compound Fertilizers",
    shortName: "NPK",
    badge: "Multi-Ratio Blends",
    category: "Compound",
    image: NPK_IMG,
    subtitle: "Balanced Multi-Nutrient Formulas for Field Crops, Hydroponics & Orchards",
    agronomicUse: "NPK is a compound fertilizer containing different ratios of nitrogen (N), phosphorus (P), and potassium (K). The element ratios vary depending on plant requirements and environmental conditions. It is used across various agricultural applications—including field crops, hydroponics, and container gardening—to boost plant productivity, quality, disease resistance, and environmental tolerance.",
    production: "Manufactured through ammoniation-granulation or pipe-reactor melt agglomeration combining ammonium phosphate, urea/ammonium nitrate, and potassium chloride or sulfate. Granules are screened, dried, cooled, and surface-conditioned with anti-caking compounds to guarantee free-flowing handling during long maritime transport.",
    chemicalFormula: "N - P₂O₅ - K₂O (Compound Complex)",
    specs: [
      { label: "CUSTOM BLENDS", value: "15-15-15, 20-20-20, 16-16-16, 12-12-17+TE" },
      { label: "GRANULE SIZE", value: "2.0mm &mdash; 4.75mm (≥ 90%)" },
      { label: "SOLUBILITY", value: "High Root Assimilation" },
      { label: "CONDITIONING", value: "Anti-Caking Polymer Coated" }
    ],
    keyHighlights: [
      "Homogeneous nutrient distribution in every single pellet",
      "Enhances vigor, root development, and fruit yield simultaneously",
      "Custom formulations adapted to regional soil testing parameters"
    ]
  },
  {
    id: "ammonium-sulfate",
    name: "Ammonium Sulfate",
    shortName: "Ammonium Sulfate",
    badge: "21% N + 24% S",
    category: "Nitrogen",
    image: AMMONIUM_SULFATE_IMG,
    subtitle: "Highly Soluble Dual-Nutrient Crystalline & Granular Fertilizer",
    agronomicUse: "Ammonium sulfate is a versatile fertilizer commonly used to supply nitrogen and sulfur. It is especially valuable for crops requiring high nitrogen levels, such as wheat, corn, and barley, and its high solubility suits diverse agricultural applications including direct soil broadcasting and fertigation.",
    production: "Produced for over 150 years, it was initially made from coal gas manufacturing ammonia or steel coal coke, but is now manufactured by reacting sulfuric acid with heated ammonia. Crystals are dried, screened, and coated with a conditioner. Most current demand is met via industrial by-products (such as nylon manufacturing co-products or spent sulfuric acid), yielding consistent, highly soluble white-to-beige crystals.",
    chemicalFormula: "(NH₄)₂SO₄ [CAS #7783-20-2]",
    specs: [
      { label: "NITROGEN (N)", value: "21.0% Minimum (Ammoniacal)" },
      { label: "SULFUR (S)", value: "24.0% Minimum (Sulfate)" },
      { label: "APPEARANCE", value: "White to Off-White Crystalline / Granular" },
      { label: "SOLUBILITY", value: "100% Water Soluble" }
    ],
    keyHighlights: [
      "Provides immediately available nitrogen and sulfate sulfur",
      "Resistant to nitrogen volatilization loss compared to urea in alkaline soils",
      "Dual application: excellent for agriculture and technical/flocculant uses"
    ]
  },
  {
    id: "urea",
    name: "Urea (Nitrogen 46%)",
    shortName: "Urea",
    badge: "46% N High Density",
    category: "Nitrogen",
    image: UREA_IMG,
    subtitle: "The World's Preeminent High-Concentration Solid Nitrogen Fertilizer",
    agronomicUse: "Urea is the most widely used solid nitrogen fertilizer globally, featuring a high N content that makes it efficient to transport and apply to crops requiring large amounts of nitrogen, such as corn, wheat, and rice. It can be applied to soil as solid prills or dissolved for foliar applications and fertigation.",
    production: "Produced through the controlled reaction of ammonia gas (NH₃) and carbon dioxide (CO₂) at elevated temperatures and pressures. Molten urea is formed into spheres via granulation equipment or hardened into solid prills. Manufacturing conditions are carefully controlled to keep biuret levels low. Plants are typically located near NH₃ facilities, and urea is distributed globally via ocean vessel, barge, rail, and truck.",
    chemicalFormula: "CO(NH₂)₂ [CAS #57-13-6]",
    specs: [
      { label: "TOTAL NITROGEN", value: "46.0% Minimum" },
      { label: "BIURET CONTENT", value: "≤ 1.0% Maximum (Low Biuret Available)" },
      { label: "MOISTURE", value: "≤ 0.5% Maximum" },
      { label: "FORMS", value: "Granular (2-4mm) & Prilled Agricultural" }
    ],
    keyHighlights: [
      "Highest nitrogen concentration of any dry solid fertilizer",
      "Optimal logistics economics due to nutrient densification",
      "Treated with conditioning agents to prevent caking during ocean voyages"
    ]
  },
  {
    id: "ssp",
    name: "Single Superphosphate (SSP)",
    shortName: "SSP",
    badge: "16%–20% P₂O₅ + 12% S",
    category: "Phosphates",
    image: SSP_IMG,
    subtitle: "Phosphate Fertilizer with Built-in Gypsum & Sulfate Sulfur",
    agronomicUse: "Single Superphosphate is an effective, phosphorus-rich fertilizer ideal for crops requiring high amounts of phosphorus, such as fruits, vegetables, and flowers. In addition to available phosphate, it provides sulfur and calcium, enhancing root architecture, flowering, and seed development.",
    production: "Ground phosphate rock reacts with sulfuric acid to form a semi-solid that cures in a den and storage pile before being milled and screened or granulated.\n\nGeneral Chemical Reaction:\nCa₃(PO₄)₂ [rock phosphate] + 2H₂SO₄ [sulfuric acid] → Ca(H₂PO₄)₂ [monocalcium phosphate] + 2CaSO₄ [gypsum].\n\nSSP contains both monocalcium phosphate and gypsum, eliminating phosphogypsum disposal issues. It is also known as ordinary or normal superphosphate.",
    chemicalFormula: "Ca(H₂PO₄)₂·H₂O + 2(CaSO₄·2H₂O)",
    specs: [
      { label: "AVAILABLE P₂O₅", value: "16.0% &mdash; 20.0%" },
      { label: "SULFUR (S)", value: "11.0% &mdash; 12.0%" },
      { label: "CALCIUM (Ca)", value: "19.0% &mdash; 21.0%" },
      { label: "PHYSICAL FORM", value: "Uniform Granular & Screened Powder" }
    ],
    keyHighlights: [
      "Contains dual plant nutrients (Phosphate + Sulfate Sulfur + Calcium)",
      "Gypsum co-product enriches soil structure without environmental waste",
      "Cost-effective solution for sulfur-deficient and phosphorus-hungry soils"
    ]
  },
  {
    id: "tsp",
    name: "Triple Superphosphate (TSP)",
    shortName: "TSP",
    badge: "46% P₂O₅ High Density",
    category: "Phosphates",
    image: TSP_IMG,
    subtitle: "Highly Concentrated Water-Soluble Phosphorus Fertilizer",
    agronomicUse: "Widely used in agriculture to promote robust growth and higher yields in crops requiring substantial phosphorus levels (such as fruits, vegetables, legumes, and flowers). Its high P concentration makes it an efficient source of phosphorus for soil preparation and bulk blending.",
    production: "Non-granular TSP is produced by reacting finely ground phosphate rock with liquid phosphoric acid in a cone-type mixer. Granular TSP is made similarly by spraying the slurry as a coating onto small particles to build desired granule sizes. Both methods require the product to cure for several weeks to complete chemical reactions.",
    chemicalFormula: "Ca(H₂PO₄)₂·H₂O (Monocalcium Phosphate)",
    specs: [
      { label: "TOTAL P₂O₅", value: "46.0% Minimum" },
      { label: "WATER SOLUBLE P₂O₅", value: "≥ 38.0% (over 85% of total P)" },
      { label: "FREE ACID", value: "≤ 4.5% Max as P₂O₅" },
      { label: "GRANULE SIZE", value: "2.0mm &mdash; 4.5mm (≥ 90%)" }
    ],
    keyHighlights: [
      "Over 40% water-soluble P for fast plant root uptake",
      "Ideal for bulk blending with Urea, Ammonium Sulfate, and Potash",
      "Significantly minimizes ocean freight cost per unit of phosphorus"
    ]
  }
];

const EMAIL_TEMPLATES = [
  {
    title: "Bulk Sulfur Procurement",
    subtitle: "For raw granular, lump, or micronized sulfur logistics",
    icon: "🔋",
    subject: "Bulk Sulfur Procurement Request - Awtad Trade Desk",
    body: `Dear Awtad Trade Desk,

We are interested in pricing and availability for bulk sulfur loads. Please provide chemical specification certificates and logistics capabilities.

Cargo Type (Granular/Lump/Micronized): 
Target Volume (MT): 
Discharge Port: 
Target Delivery Month: 
Mesh Size / Purity Specifications: 

Best regards,
[Name]
[Company]`
  },
  {
    title: "Phosphate Rock & Phosphates (PR / SSP / TSP)",
    subtitle: "For agricultural fertilizer manufacturing and direct soil application",
    icon: "⛰️",
    subject: "Phosphate Commodities Procurement Request - Awtad Trade Desk",
    body: `Dear Awtad Trade Desk,

We would like to request specifications, commercial quotation, and vessel loading schedules for phosphate commodities.

Product Required (Phosphate Rock / SSP / TSP): 
Target P2O5 Grade: 
Target Volume (MT): 
Incoterms (FOB / CIF): 
Discharge Port: 

Best regards,
[Name]
[Company]`
  },
  {
    title: "Nitrogen & Compound Fertilizers (Urea / AS / NPK)",
    subtitle: "For agricultural field crops, custom NPK blends, and global distribution",
    icon: "🌾",
    subject: "Nitrogen & NPK Fertilizer Inquiry - Awtad Trade Desk",
    body: `Dear Awtad Trade Desk,

We would like to receive product catalogs and commercial terms for nitrogenous and compound fertilizer commodities.

Product (Urea 46% / Ammonium Sulfate / NPK): 
NPK Formulation (if applicable): 
Volume Needed (MT): 
Packaging (Bulk Vessel / 50kg Bags / 1000kg Jumbo Bags): 
Target Discharge Port: 

Best regards,
[Name]
[Company]`
  }
];

export default function App() {
  const [isGenerating, setIsGenerating] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const [copiedTemplateIndex, setCopiedTemplateIndex] = useState<number | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);

  // Responsive Navigation menu toggle
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Selected filter on product page
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>("All");

  // Secure spam-proof form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState("");
  const [serverSubmissions, setServerSubmissions] = useState<any[]>([]);
  const [showSpamMonitor, setShowSpamMonitor] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDesktopDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSubmissions = async () => {
    try {
      const result = await resilientFetch("submissions");
      if (result.ok) {
        setServerSubmissions(result.data.submissions || []);
      }
    } catch (err) {
      console.error("Failed to load submissions", err);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus('idle');
    setStatusMsg("");

    try {
      const result = await resilientFetch("contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          website_url: honeypot
        })
      });

      if (result.ok) {
        setSubmissionStatus('success');
        setStatusMsg(result.data.message || "Thank you! Your message has been sent successfully.");
        setFormData({ name: "", email: "", subject: "", message: "" });
        fetchSubmissions();
      } else {
        setSubmissionStatus('error');
        setStatusMsg(result.data.message || `Submission failed with server code ${result.status}.`);
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      setSubmissionStatus('error');
      setStatusMsg(`Unable to connect to the Node.js server. Please check your cPanel Node.js application status, restart the application, and ensure that routes are active.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dedicated off-screen PDF Brochure capture mechanism using html2canvas-pro & jsPDF
  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    // Allow React to mount the off-screen PDF elements
    await new Promise(resolve => setTimeout(resolve, 350));

    try {
      if (!pdfContentRef.current) return;
      const element = pdfContentRef.current;

      // Ensure all images are loaded
      const images = Array.from(element.querySelectorAll('img')) as HTMLImageElement[];
      await Promise.all(images.map((img: HTMLImageElement) => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      }));

      await new Promise(resolve => setTimeout(resolve, 350));

      const pageElements = Array.from(element.querySelectorAll('.a4-page')) as HTMLElement[];
      if (pageElements.length === 0) {
        console.warn("No brochure pages found");
        return;
      }

      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      });

      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i];
        if (i > 0) {
          pdf.addPage('a4', 'portrait');
        }

        const canvas = await html2canvas(pageEl, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          width: 794,
          windowWidth: 794,
          onclone: (clonedDoc) => {
            const styles = clonedDoc.querySelectorAll('style');
            styles.forEach((style) => {
              if (style.innerHTML && style.innerHTML.includes('oklab(')) {
                style.innerHTML = style.innerHTML.replace(/oklab\([^)]+\)/g, '#2C5282');
              }
            });
          }
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      }

      pdf.save(`Awtad_Trade_Corporate_Profile_Cairo_Egypt_${EDITION_YEAR.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error("Brochure Generation failed:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleScrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setDesktopDropdownOpen(false);
    setTimeout(() => {
      setMobileMenuOpen(false);
    }, 150);
  };

  const handleProductInquiry = (productName: string) => {
    setFormData(prev => ({
      ...prev,
      subject: `Inquiry: Quotation & Specs for ${productName}`
    }));
    handleScrollTo('contact');
  };

  const filteredProducts = activeCategoryFilter === "All" 
    ? PRODUCTS_LIST 
    : PRODUCTS_LIST.filter(p => p.category === activeCategoryFilter);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-sulfur-gold/40">
      
      {/* PROFESSIONAL NAVIGATION BAR */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo area */}
            <div 
              onClick={() => handleScrollTo('home')} 
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="bg-slate-100 p-1.5 rounded-lg border border-slate-200/50 flex items-center justify-center h-12 w-20 overflow-hidden">
                <img 
                  src={LOGO_URL} 
                  className="h-full w-auto object-contain mix-blend-multiply brightness-125 contrast-125" 
                  alt="Awtad Trade" 
                />
              </div>
              <div className="flex flex-col">
                <span className="font-display font-bold text-deep-navy text-lg tracking-tight leading-none">AWTAD</span>
                <span className="font-technical text-[9px] uppercase tracking-widest text-[#2C5282] font-semibold mt-0.5">TRADE</span>
              </div>
            </div>

            {/* Desktop Navigation Links with Products Dropdown */}
            <div className="hidden md:flex items-center gap-7">
              <button 
                onClick={() => handleScrollTo('overview')} 
                className="text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical"
              >
                Core Overview
              </button>

              <button 
                onClick={() => handleScrollTo('ceo-message')} 
                className="text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical"
              >
                CEO Message
              </button>

              <button 
                onClick={() => handleScrollTo('supply-chain')} 
                className="text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical"
              >
                Logistics & Supply
              </button>

              {/* PRODUCTS DROPDOWN */}
              <div 
                ref={dropdownRef} 
                className="relative"
                onMouseEnter={() => setDesktopDropdownOpen(true)}
                onMouseLeave={() => setDesktopDropdownOpen(false)}
              >
                <button 
                  onClick={() => {
                    handleScrollTo('products');
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical py-2"
                >
                  <span>Products</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${desktopDropdownOpen ? 'rotate-180 text-deep-navy' : 'text-slate-400'}`} />
                </button>

                {/* Dropdown Menu */}
                {desktopDropdownOpen && (
                  <div className="absolute top-full left-0 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-[10px] font-technical uppercase font-bold tracking-widest text-slate-400">
                        Commodity Portfolio
                      </span>
                      <button 
                        onClick={() => handleScrollTo('products')} 
                        className="text-[11px] font-semibold text-industrial-blue hover:underline"
                      >
                        View All (7)
                      </button>
                    </div>

                    <div className="max-h-[380px] overflow-y-auto py-1">
                      {PRODUCTS_LIST.map((prod) => (
                        <button
                          key={prod.id}
                          onClick={() => handleScrollTo(`product-${prod.id}`)}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-start gap-3 group"
                        >
                          <div className="w-8 h-8 rounded-md overflow-hidden bg-slate-100 border border-slate-200/80 shrink-0 mt-0.5">
                            <img src={prod.image} alt={prod.shortName} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-display text-xs font-semibold text-deep-navy group-hover:text-industrial-blue truncate">
                                {prod.shortName}
                              </span>
                              <span className="text-[9px] font-technical text-slate-400 shrink-0">
                                {prod.badge}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 truncate leading-snug">
                              {prod.subtitle}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="border-t border-slate-100 px-4 pt-2.5 pb-1 bg-slate-50/60 rounded-b-xl flex items-center justify-between">
                      <span className="text-[10px] text-slate-500 font-technical">Direct ocean shipment &bull; Cairo, Egypt</span>
                      <button 
                        onClick={() => handleScrollTo('contact')}
                        className="text-[10px] font-technical font-semibold text-deep-navy hover:text-industrial-blue flex items-center gap-1"
                      >
                        RFQ Desk <ArrowUpRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button 
                onClick={() => handleScrollTo('industries')} 
                className="text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical"
              >
                Applications
              </button>

              <button 
                onClick={() => handleScrollTo('contact')} 
                className="text-sm font-medium text-slate-600 hover:text-deep-navy transition-colors font-technical"
              >
                Contact Us
              </button>

              <button
                onClick={handleGeneratePDF}
                disabled={isGenerating}
                className="px-4 py-2 bg-slate-900 hover:bg-deep-navy text-white rounded-lg text-xs font-technical font-semibold transition-all flex items-center gap-2 shadow-xs cursor-pointer disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5 text-sulfur-gold" />
                <span>{isGenerating ? "Exporting PDF..." : "Export Brochure"}</span>
              </button>
            </div>

            {/* Mobile Hamburger toggle */}
            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={handleGeneratePDF}
                disabled={isGenerating}
                className="p-2 text-slate-700 bg-slate-100 rounded-lg text-xs flex items-center gap-1.5"
                title="Export PDF"
              >
                <Download className="w-4 h-4 text-deep-navy" />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-600 hover:text-deep-navy focus:outline-none p-2 rounded-md hover:bg-slate-100"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

          </div>
        </div>

        {/* Mobile Navigation Menu Drawer */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden border-t border-slate-200 bg-white transition-all duration-200 max-h-[85vh] overflow-y-auto"
            id="mobile-drawer-menu"
          >
            <div className="px-4 pt-3 pb-6 space-y-2 flex flex-col">
              <button 
                onClick={() => handleScrollTo('overview')} 
                className="w-full text-left py-2.5 px-3 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700"
              >
                Core Overview
              </button>
              <button 
                onClick={() => handleScrollTo('ceo-message')} 
                className="w-full text-left py-2.5 px-3 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700"
              >
                Message from the CEO
              </button>
              <button 
                onClick={() => handleScrollTo('supply-chain')} 
                className="w-full text-left py-2.5 px-3 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700"
              >
                Logistics & Supply
              </button>

              {/* Mobile Products Accordion */}
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50">
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  className="w-full text-left py-3 px-3.5 font-medium text-sm text-deep-navy flex justify-between items-center bg-white"
                >
                  <span className="font-semibold">Products Range (7 Commodities)</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                </button>

                {mobileProductsOpen && (
                  <div className="px-2 py-2 space-y-1 bg-slate-50 border-t border-slate-200">
                    <button
                      onClick={() => handleScrollTo('products')}
                      className="w-full text-left py-2 px-3 rounded text-xs font-semibold text-industrial-blue hover:bg-white"
                    >
                      &rarr; View Complete Portfolio Section
                    </button>
                    {PRODUCTS_LIST.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => handleScrollTo(`product-${p.id}`)}
                        className="w-full text-left py-2 px-3 rounded text-xs text-slate-700 hover:bg-white flex items-center justify-between"
                      >
                        <span className="font-medium">{p.shortName}</span>
                        <span className="text-[10px] text-slate-400 font-technical">{p.badge}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button 
                onClick={() => handleScrollTo('industries')} 
                className="w-full text-left py-2.5 px-3 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700"
              >
                Applications & Industries
              </button>
              <button 
                onClick={() => handleScrollTo('contact')} 
                className="w-full text-left py-2.5 px-3 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700"
              >
                Contact Us & RFQ Desk
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* SECTION 1: HERO CONTAINER */}
      <section id="home" className="scroll-mt-20 relative bg-slate-900 overflow-hidden min-h-[580px] flex items-center py-20 px-4 md:px-8">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMG} 
            alt="Awtad Trade Industrial Site" 
            className="absolute inset-0 w-full h-full object-cover opacity-35 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-deep-navy via-deep-navy/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8 flex flex-col justify-center">
            
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2.5 bg-silver px-3 py-1.5 bg-white/10 rounded-full border border-white/20 text-sulfur-gold text-[10px] font-technical uppercase tracking-widest mb-6 w-fit"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-sulfur-gold animate-pulse"></div>
              <span>Global Sulfur &amp; Fertilizer Commodities Network</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white mb-6 tracking-tighter"
            >
              Excellence in Global <br />
              <span className="text-sulfur-gold">Sulfur &amp; Commodity</span> Supply Chains
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/80 max-w-2xl text-base sm:text-lg mb-8 leading-relaxed"
            >
              Awtad Trade is a premier institutional force in industrial commodities. We optimize raw material procurement, processing high-capacity supplies of industrial sulfur, phosphate rock, NPK compounds, ammonium sulfate, urea, SSP, and TSP for vertical integrations worldwide with strategic marine logistical precision.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <button 
                onClick={() => handleScrollTo('contact')}
                className="px-6 py-3.5 bg-sulfur-gold hover:bg-yellow-500 text-deep-navy rounded-lg font-semibold text-sm transition-all shadow-md flex items-center gap-2 group cursor-pointer"
              >
                <span>Request Quotation</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button 
                onClick={() => handleScrollTo('products')}
                className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/30 font-semibold text-sm transition-all flex items-center gap-2 cursor-pointer"
              >
                <span>Explore 7 Product Ranges</span>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: CORPORATE OVERVIEW */}
      <section id="overview" className="scroll-mt-20 py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Context Left */}
            <div className="lg:col-span-7">
              <span className="font-technical uppercase text-xs font-bold tracking-[0.2em] text-[#2C5282] block mb-2">
                Awtad Trade Overview
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-deep-navy mb-6 tracking-tight">
                Empowering the World's Core Energy &amp; Agricultural Industries
              </h2>
              <div className="w-16 h-1 bg-sulfur-gold mb-8"></div>
              
              <div className="space-y-6 text-slate-600 leading-relaxed text-base">
                <p>
                  Awtad Trade stands as a premier institutional force in the global energy, chemical, and agricultural raw materials sector. With decades of combined expertise in sulfur logistics, phosphate mining supply, and macro-fertilizer distribution, we have built a reputation for unwavering reliability and strategic precision.
                </p>
                <p>
                  Our corporate philosophy merges the structural integrity of top-tier financial management with the high-performance execution required in global bulk shipping. We serve as the vital link between massive refinement and synthesis capacities and demanding industrial end-users across six continents.
                </p>
                <p className="font-semibold text-deep-navy">
                  Leveraging our worldwide logistics connections, we deliver exceptional industrial value through chemical purity verification, portside terminal stockpiling, and ethical trading protocols.
                </p>
              </div>
            </div>

            {/* Core Values Right */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              
              <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="p-3 bg-indigo-50 text-industrial-blue rounded-lg w-fit mb-4">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-deep-navy text-lg mb-1">Uncompromising Integrity</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  We guarantee full compliance with global safety standards, ASTM norms, and ethical trading protocols across all jurisdictions and marine channels.
                </p>
              </div>

              <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="p-3 bg-indigo-50 text-industrial-blue rounded-lg w-fit mb-4">
                  <Activity className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-deep-navy text-lg mb-1">Operational Efficiency</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Through custom automated logistics pipeline systems, we minimize lead times, maximize volume capacities, and eliminate maritime demurrage friction.
                </p>
              </div>

              <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="p-3 bg-indigo-50 text-industrial-blue rounded-lg w-fit mb-4">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-deep-navy text-lg mb-1">Strategic Supply Networks</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  We connect major refining and synthesis hubs directly to bulk delivery points, maintaining robust stockpiles against commodity price and supply fluctuations.
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* SECTION: MESSAGE FROM THE CEO */}
      <section id="ceo-message" className="scroll-mt-20 py-20 md:py-28 bg-gradient-to-b from-slate-50 via-white to-slate-50 border-b border-slate-200 relative overflow-hidden">
        {/* Subtle decorative background accents */}
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-96 h-96 bg-sulfur-gold/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -left-20 bottom-10 w-80 h-80 bg-industrial-blue/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Main Message Column */}
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-deep-navy/5 border border-deep-navy/10 rounded-full text-deep-navy text-[11px] font-technical uppercase tracking-widest mb-4">
                <Award className="w-3.5 h-3.5 text-sulfur-gold" />
                <span>Executive Leadership &bull; Corporate Vision</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-deep-navy mb-4 tracking-tight">
                Message from the CEO
              </h2>
              <div className="w-20 h-1.5 bg-sulfur-gold mb-8"></div>

              {/* Quotation card container */}
              <div className="bg-white p-8 sm:p-10 rounded-2xl border border-slate-200/90 shadow-sm relative">
                <Quote className="w-12 h-12 text-sulfur-gold/30 mb-4" />
                
                <div className="space-y-5 text-slate-700 leading-relaxed text-base sm:text-lg">
                  <p className="font-normal text-slate-800">
                    At Awtad Trade, we take pride in being a trusted international trade partner, delivering high-quality strategic commodities, industrial materials, and agricultural products across global markets.
                  </p>
                  
                  <p className="font-normal text-slate-800">
                    Our foundation is built on transparency, uncompromised quality, and reliable execution. We are dedicated to offering integrated supply solutions that meet the evolving demands of our clients and support growth across international markets.
                  </p>
                  
                  <p className="font-normal text-slate-800">
                    We believe in long-term strategic growth and strive to cultivate strong, mutually beneficial partnerships with our global clients and suppliers.
                  </p>
                </div>

                {/* Sign-off Block */}
                <div className="mt-8 pt-8 border-t border-slate-100 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                  <div>
                    <span className="block text-sm font-serif italic text-slate-500 mb-2">Sincerely,</span>
                    <h3 className="font-display text-2xl font-bold text-deep-navy tracking-tight">
                      Dr. Salma Salah
                    </h3>
                    <p className="font-technical text-xs uppercase tracking-wider text-[#2C5282] font-semibold mt-1">
                      Chief Executive Officer – Awtad Trade
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-slate-50 p-2 rounded border border-slate-200/80 flex items-center justify-center h-10 w-20">
                      <img 
                        src={LOGO_URL} 
                        className="h-full w-auto object-contain mix-blend-multiply brightness-125 contrast-125" 
                        alt="Awtad Trade Corporate Seal" 
                      />
                    </div>
                    <div className="text-[10px] font-technical uppercase tracking-wider text-slate-400">
                      <span>Executive Office</span>
                      <span className="block text-slate-600 font-semibold">Cairo, Egypt</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Executive Credentials & Core Pillars */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              
              <div className="bg-deep-navy text-white p-8 rounded-2xl shadow-lg border border-slate-800 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
                  <Shield className="w-full h-full text-white" />
                </div>
                
                <span className="text-[10px] font-technical uppercase tracking-widest text-sulfur-gold font-bold block mb-2">
                  Corporate Tenets
                </span>
                <h4 className="font-display text-xl font-bold mb-4 text-white">
                  Leadership Commitments
                </h4>
                
                <div className="space-y-4 text-sm text-slate-300">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-sulfur-gold mt-1.5 shrink-0"></div>
                    <div>
                      <strong className="text-white block font-medium">Transparency</strong>
                      <span className="text-xs text-slate-400">Clear governance, open documentation, and ethical trade relationships worldwide.</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-sulfur-gold mt-1.5 shrink-0"></div>
                    <div>
                      <strong className="text-white block font-medium">Uncompromised Quality</strong>
                      <span className="text-xs text-slate-400">Strict ASTM/ISO conformity and purity standards across every commodity line.</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-sulfur-gold mt-1.5 shrink-0"></div>
                    <div>
                      <strong className="text-white block font-medium">Reliable Execution</strong>
                      <span className="text-xs text-slate-400">End-to-end supply chain mastery from refining origin to vessel discharge.</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <button 
                    onClick={() => handleScrollTo('contact')}
                    className="w-full py-2.5 px-4 bg-sulfur-gold hover:bg-yellow-400 text-deep-navy rounded-lg font-technical font-semibold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                  >
                    <span>Connect with Awtad Trade</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Direct email card for executive inquiry */}
              <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
                <span className="text-[10px] font-technical uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                  Official Communication
                </span>
                <div className="text-sm font-semibold text-deep-navy mb-2">
                  Executive &amp; Commercial Desk
                </div>
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <SecureEmail className="font-mono text-xs text-industrial-blue font-semibold hover:underline" />
                    <span className="text-[10px] text-slate-400 font-technical">Cairo Headquarters</span>
                  </div>
                  <div className="text-[11px] text-slate-600 pt-2 border-t border-slate-200/80 flex items-start gap-1.5 font-technical">
                    <MapPin className="w-3.5 h-3.5 text-industrial-blue shrink-0 mt-0.5" />
                    <span>4 Makram Ebeid Street, Nasr City - Cairo - Egypt</span>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* SECTION 3: PRODUCTION & LOGISTICS */}
      <section id="supply-chain" className="scroll-mt-20 py-20 md:py-28 bg-deep-navy text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
          <Shield className="w-full h-full text-white" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="max-w-3xl mb-16">
            <span className="font-technical text-sulfur-gold text-xs font-bold uppercase tracking-[0.3em] block mb-2">
              Logistics Infrastructure
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-white">
              Sulfur &amp; Commodity Supply Chain Mastery
            </h2>
            <p className="text-white/80 text-base sm:text-lg">
              Optimizing the journey of elemental sulfur, raw phosphate rock, and bulk fertilizers from extraction and synthesis to direct oceanic discharge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            
            <div className="bg-white/5 border border-white/15 p-8 rounded-xl backdrop-blur-xs hover:border-sulfur-gold/40 transition-colors">
              <h3 className="font-display font-bold text-xl text-white mb-2">Scalable Output</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                We distribute elemental sulfur in crushed bulk, loose granules, and high-temp molten liquid, alongside thousands of tons of high-analysis phosphate and nitrogen fertilizers under strict lab testing.
              </p>
              <div className="flex items-center gap-2 text-xs font-technical uppercase text-sulfur-gold">
                <Check className="w-4 h-4" />
                <span>ASTM &amp; ISO Quality Certified</span>
              </div>
            </div>

            <div className="bg-white/5 border border-white/15 p-8 rounded-xl backdrop-blur-xs hover:border-sulfur-gold/40 transition-colors">
              <h3 className="font-display font-bold text-xl text-white mb-2">Port Granulation &amp; Blending</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                Deploying portside processing equipment to refine crushed materials directly from crude stockpiles into easy-to-use premium agricultural granules and custom NPK compounds.
              </p>
              <div className="flex items-center gap-2 text-xs font-technical uppercase text-sulfur-gold">
                <Check className="w-4 h-4" />
                <span>Dynamic Dust Suppression</span>
              </div>
            </div>

            <div className="bg-white/5 border border-white/15 p-8 rounded-xl backdrop-blur-xs hover:border-sulfur-gold/40 transition-colors">
              <h3 className="font-display font-bold text-xl text-white mb-2">Strategic Stockpiles</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                Commanding large-capacity terminal warehouses inside major Mediterranean, Red Sea, and Middle Eastern shipping hubs to guarantee uninterrupted delivery schedules for industrial pipelines.
              </p>
              <div className="flex items-center gap-2 text-xs font-technical uppercase text-sulfur-gold">
                <Check className="w-4 h-4" />
                <span>Automated Reserve Controls</span>
              </div>
            </div>

          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-8 backdrop-blur-xs">
            <div className="max-w-2xl">
              <h4 className="font-display font-bold text-lg mb-2 text-sulfur-gold">Preventing Environmental Hazards &amp; Boosting Sustainable Agriculture</h4>
              <p className="text-sm text-white/80 leading-relaxed">
                The absolute majority of our high-quality Sulfur is recovered from natural gas sweetening and refinery hydro-processing. We safely convert industrial by-products into high-utility resources that nourish agricultural soils and power world chemical syntheses.
              </p>
            </div>
            <div className="bg-sulfur-gold text-deep-navy font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider font-technical shrink-0">
              Verified Green Processing
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 4: PRODUCT PORTFOLIO */}
      <section id="products" className="scroll-mt-20 py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="font-technical text-[#2C5282] uppercase text-xs font-bold tracking-[0.2em] mb-2 block">
              Commodity Portfolio
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-deep-navy tracking-tight">
              Industrial Sulfur &amp; Fertilizer Range
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
              Explore our seven primary commodity product lines engineered for agricultural yields, chemical acidulation, and heavy industrial applications.
            </p>
            <div className="w-16 h-1 bg-sulfur-gold mx-auto mt-4"></div>
          </div>

          {/* Interactive Category Filter Bar */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-14">
            {["All", "Sulfur", "Phosphates", "Nitrogen", "Compound"].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategoryFilter(cat)}
                className={`px-4 py-2 rounded-lg text-xs font-technical font-semibold transition-all cursor-pointer ${
                  activeCategoryFilter === cat
                    ? "bg-deep-navy text-white shadow-xs"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                }`}
              >
                {cat === "All" ? "All Commodities (7)" : `${cat}`}
              </button>
            ))}
          </div>

          {/* PRODUCT CARDS LIST */}
          <div className="space-y-16">
            {filteredProducts.map((product, idx) => (
              <div 
                key={product.id}
                id={`product-${product.id}`}
                className="scroll-mt-24 bg-slate-50 border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-shadow"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                  
                  {/* Left Column: High-Res Image & Visual Identifier */}
                  <div className="lg:col-span-5 relative bg-slate-900 min-h-[300px] lg:min-h-full overflow-hidden flex flex-col justify-end">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent"></div>
                    
                    <div className="relative z-10 p-6 sm:p-8 text-white">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-md rounded-md text-[10px] font-technical uppercase tracking-widest text-sulfur-gold border border-white/20 mb-2">
                        <span>{product.category}</span>
                        <span>&bull;</span>
                        <span>{product.badge}</span>
                      </div>
                      <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight text-white">
                        {product.name}
                      </h3>
                      {product.chemicalFormula && (
                        <p className="text-white/70 font-technical text-xs mt-1 font-mono">
                          {product.chemicalFormula}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Technical Details, Agronomic Use, Production, and Specs */}
                  <div className="lg:col-span-7 p-6 sm:p-10 flex flex-col justify-between">
                    <div>
                      <div className="mb-6">
                        <span className="text-xs uppercase tracking-widest font-technical text-slate-400 font-bold block mb-1">
                          Agronomic Use &amp; Application
                        </span>
                        <p className="text-slate-700 text-sm leading-relaxed">
                          {product.agronomicUse}
                        </p>
                      </div>

                      <div className="mb-6">
                        <span className="text-xs uppercase tracking-widest font-technical text-slate-400 font-bold block mb-1">
                          Production &amp; Extraction
                        </span>
                        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed whitespace-pre-line">
                          {product.production}
                        </p>
                      </div>

                      {/* Technical Specs Grid */}
                      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 mb-6">
                        <div className="text-[10px] font-technical uppercase font-bold text-deep-navy tracking-wider mb-3">
                          Verified Technical Specifications
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {product.specs.map((spec, sIdx) => (
                            <div key={sIdx} className="border-l-2 border-sulfur-gold pl-2.5">
                              <span className="block text-[9px] font-technical uppercase text-slate-400 font-semibold tracking-wider">
                                {spec.label}
                              </span>
                              <strong className="text-xs sm:text-sm font-display text-deep-navy" dangerouslySetInnerHTML={{ __html: spec.value }} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Highlights */}
                      <div className="space-y-1.5 mb-6">
                        {product.keyHighlights.map((hl, hIdx) => (
                          <div key={hIdx} className="flex items-center gap-2 text-xs text-slate-600 font-technical">
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span>{hl}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bottom CTA Row */}
                    <div className="border-t border-slate-200/80 pt-6 flex flex-wrap items-center justify-between gap-4">
                      <div className="text-xs text-slate-500 font-technical">
                        Certified commercial lots ready for bulk vessel booking
                      </div>
                      <button
                        onClick={() => handleProductInquiry(product.name)}
                        className="px-5 py-2.5 bg-deep-navy hover:bg-industrial-blue text-white rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors cursor-pointer shadow-xs"
                      >
                        <span>Inquire Specifications &amp; Pricing</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                  </div>

                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 5: INDUSTRIAL APPLICATIONS */}
      <section id="industries" className="scroll-mt-20 py-20 md:py-28 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-technical text-[#2C5282] uppercase text-xs font-bold tracking-[0.2em] mb-2 block">
              Core Applications
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-deep-navy tracking-tight">
              Essential End-Uses of Awtad Sulfur &amp; Commodities
            </h2>
            <p className="text-slate-500 font-medium text-sm mt-2">
              Powering global agricultural nutrition and industrial value loops through elemental trade.
            </p>
            <div className="w-16 h-1 bg-sulfur-gold mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">🌾</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Agriculture &amp; Fertilizers</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Critical base ingredient for high-grade phosphate farming fertilizers, NPK formulas, Single and Triple Superphosphates, and nitrogen nutrient compounds worldwide.
              </p>
            </div>

            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">🧪</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Chemical Manufacturing</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Primary precursor element used to produce high-density industrial sulfuric acid, essential for detergents, metals extraction, phosphoric acid production, and pigment synthesis.
              </p>
            </div>

            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">🚗</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Rubber &amp; Vulcanization</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Indispensable micronized sulfur additive used to cure synthetic rubber compounds, creating resilient polymers for automotive tires and industrial conveyor belts.
              </p>
            </div>

            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">💊</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Pharmaceutical Formulations</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                High-purity technical additives utilized in active pharmaceutical processes, manufacturing essential dermatological and anti-microbial components.
              </p>
            </div>

            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">🔋</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Energy Storage &amp; Tech</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Integral element driving emerging sustainable lithium-sulfur battery tech configurations, which yield super-high volumetric densification capacities.
              </p>
            </div>

            <div className="bg-white p-8 border border-slate-200 rounded-2xl shadow-xs hover:border-deep-navy/30 transition-colors">
              <span className="text-2xl mb-4 block">🏗️</span>
              <h3 className="font-display font-bold text-deep-navy text-lg mb-2">Construction Polymers</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Advanced structural binding compounds utilized to produce specialized high-durability sulfur concrete mixtures and premium marine asphalt.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* SECTION 6: SECURE CONTACT INTERACTIVE PORTAL */}
      <section id="contact" className="scroll-mt-20 py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
            
            {/* Header portion */}
            <div className="bg-deep-navy px-8 py-12 text-white relative overflow-hidden">
              <div className="relative z-10 max-w-xl">
                <span className="font-technical text-sulfur-gold text-xs font-bold uppercase tracking-[0.25em] mb-2 block">
                  Secure Communication Console
                </span>
                <h2 className="font-display text-3xl font-bold mb-3 tracking-tight">
                  Initiate Trade Desk Inquiry
                </h2>
                <p className="text-white/80 text-sm leading-relaxed">
                  Send your requirements directly to <SecureEmail strong={true} />. All inquiries are filtered through multi-layered submission engines to prevent automated network spam.
                </p>
              </div>
              <div className="absolute top-1/2 -right-10 -translate-y-1/2 opacity-5 pointer-events-none">
                <Lock className="w-56 h-56" />
              </div>
            </div>

            <div className="p-8 sm:p-12">
              <div className="text-center max-w-xl mx-auto mb-10">
                <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3.5 py-1.5 rounded-full font-semibold mb-4">
                  <Shield className="w-3.5 h-3.5" /> Connection Fully Encrypted &amp; Spam Protected
                </span>
                <p className="text-xs text-slate-400 font-technical uppercase tracking-wider mb-6">
                  Official Trade desk direct contact point
                </p>

                {/* Secure interactive email box */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 sm:p-8 relative overflow-hidden shadow-xs">
                  <div className="absolute top-0 right-0 p-3 opacity-5 pointer-events-none">
                    <Mail className="w-20 h-20 text-deep-navy" />
                  </div>
                  <span className="block text-xs uppercase tracking-widest font-technical text-slate-500 font-bold mb-3">
                    Corporate Email Address
                  </span>
                  
                  {/* Anti-Harvest split display */}
                  <div className="inline-flex items-center justify-center p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs mb-5 font-display">
                    <span className="font-semibold text-lg sm:text-2xl text-deep-navy tracking-wide select-all flex items-center">
                      <span>info</span>
                      <span className="hidden" style={{ display: 'none' }}>antispam-bot</span>
                      <span className="text-industrial-blue mx-0.5 font-bold">@</span>
                      <span>awtadtrade.com</span>
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <button
                      onClick={() => {
                        window.location.href = "mailto:info@awtadtrade.com";
                      }}
                      className="w-full sm:w-auto bg-deep-navy hover:bg-industrial-blue text-white px-5 py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Open Mail Client</span>
                    </button>
                    <button
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText("info@awtadtrade.com");
                          setEmailCopied(true);
                          setTimeout(() => setEmailCopied(false), 3000);
                        } catch (e) {
                          console.error("Copy failed", e);
                        }
                      }}
                      className="w-full sm:w-auto border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-5 py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs"
                    >
                      {emailCopied ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-600 animate-in zoom-in-50" />
                          <span className="text-emerald-700">Email Copied!</span>
                        </>
                      ) : (
                        <>
                          <Bookmark className="w-4 h-4" />
                          <span>Copy Address</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Corporate Address & Headquarters Card */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 sm:p-7 text-left mt-6 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-deep-navy text-sulfur-gold rounded-xl flex items-center justify-center shrink-0 shadow-xs">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase tracking-widest font-technical text-slate-500 font-bold mb-1">
                        Corporate Headquarters &amp; Physical Address
                      </span>
                      <h4 className="font-display font-bold text-deep-navy text-lg leading-snug">
                        4 Makram Ebeid Street, Nasr City - Cairo - Egypt
                      </h4>
                      <p className="text-xs text-slate-500 mt-1">
                        Commercial Trading Desk &bull; Direct access to Mediterranean &amp; Red Sea maritime export corridors
                      </p>
                    </div>
                  </div>
                  <div className="sm:text-right shrink-0">
                    <span className="text-[10px] font-technical uppercase tracking-wider text-slate-400 font-semibold block">
                      Jurisdiction &amp; Registry
                    </span>
                    <span className="text-xs font-semibold text-deep-navy">Cairo, Egypt</span>
                  </div>
                </div>
              </div>

              {/* Direct Web Form */}
              <div className="border-t border-slate-100 pt-8 mt-4">
                <h3 className="font-display font-bold text-center text-deep-navy text-lg mb-2">
                  Send Direct Inquiry Message
                </h3>
                <p className="text-center text-xs text-slate-500 max-w-md mx-auto mb-6">
                  Submit your commodity volume requirements directly to our commercial operations team.
                </p>

                <form onSubmit={handleContactSubmit} className="space-y-4 max-w-xl mx-auto">
                  {/* Honeypot for bot detection */}
                  <input 
                    type="text" 
                    name="website_url" 
                    value={honeypot} 
                    onChange={(e) => setHoneypot(e.target.value)} 
                    style={{ display: 'none' }} 
                    tabIndex={-1} 
                    autoComplete="off" 
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-technical uppercase font-semibold text-slate-600 mb-1">
                        Full Name *
                      </label>
                      <input 
                        type="text" 
                        required 
                        placeholder="e.g. John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-industrial-blue/20 focus:border-industrial-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-technical uppercase font-semibold text-slate-600 mb-1">
                        Corporate Email *
                      </label>
                      <input 
                        type="email" 
                        required 
                        placeholder="procurement@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-industrial-blue/20 focus:border-industrial-blue"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-technical uppercase font-semibold text-slate-600 mb-1">
                      Inquiry Subject *
                    </label>
                    <input 
                      type="text" 
                      required 
                      placeholder="e.g. Sulfur Granular bulk order CIF Alexandria"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-industrial-blue/20 focus:border-industrial-blue"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-technical uppercase font-semibold text-slate-600 mb-1">
                      Requirements &amp; Tonnage *
                    </label>
                    <textarea 
                      rows={4}
                      required 
                      placeholder="Specify commodity, target volume (MT), delivery schedule, discharge port, and packaging preferences..."
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-industrial-blue/20 focus:border-industrial-blue"
                    />
                  </div>

                  {statusMsg && (
                    <div className={`p-3 rounded-lg text-xs ${
                      submissionStatus === 'success' 
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}>
                      {statusMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-deep-navy hover:bg-industrial-blue text-white rounded-lg font-semibold text-sm transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <span>Transmitting Encrypted Message...</span>
                    ) : (
                      <>
                        <span>Submit Procurement Inquiry</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Quick trade actions */}
              <div className="border-t border-slate-100 pt-10 mt-10">
                <h3 className="font-display font-bold text-center text-deep-navy text-lg mb-2">
                  ⚡ Quick Action Procurement Templates
                </h3>
                <p className="text-center text-xs text-slate-500 max-w-md mx-auto mb-6">
                  Select a procurement track below. Clicking automatically copies structured requirements guidelines to your clipboard and opens your mail companion.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {EMAIL_TEMPLATES.map((tmpl, idx) => {
                    const isCopied = copiedTemplateIndex === idx;
                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          const fullEmail = "info@awtadtrade.com";
                          const mailtoUrl = `mailto:${fullEmail}?subject=${encodeURIComponent(tmpl.subject)}&body=${encodeURIComponent(tmpl.body)}`;
                          
                          try {
                            navigator.clipboard.writeText(`Subject: ${tmpl.subject}\n\n${tmpl.body}`);
                            setCopiedTemplateIndex(idx);
                            setTimeout(() => setCopiedTemplateIndex(null), 3000);
                          } catch (err) {
                            console.error("Clipboard copy failed", err);
                          }

                          window.location.href = mailtoUrl;
                        }}
                        className="bg-slate-50 hover:bg-slate-100/80 hover:border-slate-300 border border-slate-200/70 p-5 rounded-xl cursor-pointer transition-all hover:scale-[1.015] flex flex-col justify-between text-left group"
                      >
                        <div>
                          <span className="text-2xl mb-3 block">{tmpl.icon}</span>
                          <h4 className="font-display font-semibold text-deep-navy group-hover:text-industrial-blue transition-colors text-sm mb-1">
                            {tmpl.title}
                          </h4>
                          <p className="text-xs text-slate-500 leading-normal mb-4">
                            {tmpl.subtitle}
                          </p>
                        </div>
                        
                        <div className="text-[10px] font-technical uppercase tracking-wider font-semibold border-t border-slate-200/50 pt-3 flex items-center justify-between">
                          {isCopied ? (
                            <span className="text-emerald-700 font-bold flex items-center gap-1.5 animate-in zoom-in-50">
                              <Check className="w-3.5 h-3.5" /> Template Copied!
                            </span>
                          ) : (
                            <span className="text-slate-400 group-hover:text-deep-navy transition-colors flex items-center gap-1">
                              Launch Desk <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800 font-technical">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 rounded flex items-center justify-center h-8 w-12 overflow-hidden">
              <img src={LOGO_URL} className="h-full w-auto object-contain mix-blend-multiply brightness-125 contrast-125" alt="Icon" />
            </div>
            <div>
              <span className="block text-white font-bold tracking-wider text-sm select-none">AWTAD TRADE</span>
              <span className="block text-[10px] text-zinc-400 uppercase">Global Commodities &bull; Cairo, Egypt</span>
              <span className="block text-[10px] text-zinc-500 mt-0.5">4 Makram Ebeid Street, Nasr City - Cairo - Egypt</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-400">
            <button onClick={() => handleScrollTo('home')} className="hover:text-white transition-colors">Home</button>
            <button onClick={() => handleScrollTo('overview')} className="hover:text-white transition-colors font-medium">About</button>
            <button onClick={() => handleScrollTo('ceo-message')} className="hover:text-white transition-colors">CEO Message</button>
            <button onClick={() => handleScrollTo('products')} className="hover:text-white transition-colors">Products</button>
            <button onClick={() => handleScrollTo('industries')} className="hover:text-white transition-colors">Industries</button>
            <SecureEmail className="hover:text-white transition-colors select-all" />
          </div>

          <div className="text-right text-[10px] text-zinc-500">
            <p>&copy; {new Date().getFullYear()} Awtad Trade. All Rights Reserved.</p>
            <div className="flex justify-end gap-2 mt-1">
              <span>Commercial Trade Registry &bull; Cairo, Egypt</span>
              <span>&bull;</span>
              <button 
                onClick={() => {
                  setShowSpamMonitor(!showSpamMonitor);
                  if(!showSpamMonitor) fetchSubmissions();
                }} 
                className="hover:text-sulfur-gold underline transition-colors"
              >
                Inquiry Logger
              </button>
            </div>
          </div>

        </div>

        {/* INQUIRY LOG VIEWER FOR TESTING */}
        {showSpamMonitor && (
          <div className="max-w-7xl mx-auto mt-8 border-t border-slate-800 pt-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-yellow-500 text-xs font-bold font-technical uppercase flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4" />
                Live anti-spam monitor logs (Server validation)
              </span>
              <button onClick={fetchSubmissions} className="text-slate-400 hover:text-white text-xs underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> reload console
              </button>
            </div>

            {serverSubmissions.length === 0 ? (
              <div className="bg-slate-950 p-4 rounded text-center text-xs text-slate-500 italic">
                No secure messages recorded in this server session yet. Test the contact form to verify.
              </div>
            ) : (
              <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 space-y-2.5 max-h-48 overflow-y-auto">
                {serverSubmissions.slice().reverse().map((sub) => (
                  <div key={sub.id} className="text-xs p-2.5 border-b border-slate-800 last:border-b-0 flex justify-between items-center bg-slate-900/50">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-white">{sub.name}</strong>
                        <span className="text-slate-500">&lt;{sub.email}&gt;</span>
                        <span className="text-slate-500">IP: {sub.ip}</span>
                      </div>
                      <span className="text-slate-400 italic font-mono block">Sub: {sub.subject} &mdash; "{sub.message}"</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      sub.status === "Spam Blocked" ? "bg-red-900/40 text-red-400 border border-red-800/50" : "bg-green-900/40 text-green-400 border border-green-800/50"
                    }`}>
                      {sub.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </footer>

      {/* ========================================== */}
      {/* PERFECTLY FORMATTED DEDICATED A4 PDF BROCHURE CANVAS */}
      {/* ========================================== */}
      {isGenerating && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <main 
            ref={pdfContentRef} 
            className="flex flex-col gap-0 !w-[794px] !p-0 !m-0 overflow-hidden shadow-none bg-white"
            style={{ width: '794px' }}
          >
            
            {/* PAGE 1: COVER PAGE */}
            <section 
              className="a4-page relative overflow-hidden bg-[#002046] flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <div className="absolute inset-0 z-0">
                <img 
                  src={HERO_IMG} 
                  alt="Industrial Maritime Logistics" 
                  className="w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#002046]/90 via-[#002046]/80 to-[#002046]"></div>
              </div>
                
              <div className="relative z-10 p-14 flex items-center justify-between border-b border-white/10">
                <div className="bg-white p-3 rounded-lg flex items-center justify-center h-16 w-36 shadow-lg">
                  <img 
                    src={LOGO_URL} 
                    alt="Awtad Trade Logo" 
                    className="max-w-full max-h-full object-contain mix-blend-multiply brightness-125 contrast-125" 
                  />
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-technical uppercase tracking-[0.25em] text-[#F2C94C] font-bold block">
                    Institutional Profile
                  </span>
                  <span className="text-xs font-technical text-white/80">
                    Official Corporate Publication &bull; {EDITION_YEAR}
                  </span>
                </div>
              </div>

              <div className="relative z-10 px-14 py-8 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full border border-white/20 text-[#F2C94C] text-[10px] font-technical uppercase tracking-widest mb-6 w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F2C94C]"></span>
                  <span>International Commodities &bull; Industrial Chemicals &bull; Agricultural Fertilizers</span>
                </div>

                <h1 className="font-display text-5xl font-bold leading-tight text-white mb-4 tracking-tight">
                  Awtad Trade
                </h1>
                <div className="w-24 h-1.5 bg-[#F2C94C] mb-6"></div>

                <p className="font-technical text-white/95 uppercase tracking-[0.3em] text-lg font-semibold mb-4">
                  Global Supply Chain Excellence
                </p>

                <p className="text-white/80 text-sm max-w-xl leading-relaxed mb-8">
                  A premier institutional force in strategic commodities and agricultural nutrient distribution. Optimizing maritime supply chains across elemental sulfur, phosphate minerals, and high-analysis nitrogen and compound fertilizers worldwide.
                </p>

                {/* Scope pill tags */}
                <div className="grid grid-cols-2 gap-3 max-w-lg mb-8">
                  <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-xs text-white/90 font-technical flex items-center gap-2">
                    <span className="text-[#F2C94C]">&bull;</span> Industrial Sulfur (Granular &amp; Micronized)
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-xs text-white/90 font-technical flex items-center gap-2">
                    <span className="text-[#F2C94C]">&bull;</span> Phosphate Rock &amp; Concentrates
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-xs text-white/90 font-technical flex items-center gap-2">
                    <span className="text-[#F2C94C]">&bull;</span> Superphosphates (SSP &amp; TSP)
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-xs text-white/90 font-technical flex items-center gap-2">
                    <span className="text-[#F2C94C]">&bull;</span> Nitrogen &amp; NPK Compounds (Urea / AS / NPK)
                  </div>
                </div>

                {/* Corporate Address & Contact Banner */}
                <div className="p-5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/15 max-w-xl">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#F2C94C] shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[9px] font-technical uppercase tracking-widest text-[#F2C94C] font-bold block mb-0.5">
                        Headquarters &bull; Cairo, Egypt
                      </span>
                      <p className="text-white font-medium text-xs leading-normal">
                        4 Makram Ebeid Street, Nasr City - Cairo - Egypt
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-[10px] text-white/70 font-technical">
                        <span>Email: info@awtadtrade.com</span>
                        <span>&bull;</span>
                        <span>Web: www.awtadtrade.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative z-10 px-14 py-6 border-t border-white/10 flex justify-between items-center text-white/60 text-[10px] font-technical uppercase tracking-wider">
                <span>Commercial Trade Registry &bull; Cairo, Egypt</span>
                <span>Document Reference: AT-CORP-2026-A4</span>
              </div>
            </section>

            {/* PAGE 2: CORPORATE OVERVIEW & MESSAGE FROM THE CEO */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Corporate Overview & Leadership" category="Executive Statement" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden">
                {/* Corporate Overview Narrative */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-1 bg-[#F2C94C]"></div>
                    <h2 className="font-display text-2xl font-bold text-[#002046]">Modern Industrialism in Global Commodity Trade</h2>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-4">
                    Awtad Trade stands as a premier institutional force in the global energy, chemical, and agricultural raw materials sector. With decades of combined expertise in sulfur and fertilizer logistics, we have built a reputation for unwavering reliability and strategic precision. We serve as the vital link between massive refinement/mining capacities and the world's most demanding industrial end-users.
                  </p>

                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <Shield className="w-5 h-5 text-[#2C5282] mb-1.5" />
                      <strong className="text-xs text-[#002046] block mb-0.5">Transparency</strong>
                      <p className="text-[10px] text-slate-500 leading-tight">Clear governance, verified documentation &amp; ethical trade worldwide.</p>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <Award className="w-5 h-5 text-[#2C5282] mb-1.5" />
                      <strong className="text-xs text-[#002046] block mb-0.5">Quality</strong>
                      <p className="text-[10px] text-slate-500 leading-tight">Strict ASTM/ISO conformity and purity guarantees on all shipments.</p>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <Truck className="w-5 h-5 text-[#2C5282] mb-1.5" />
                      <strong className="text-xs text-[#002046] block mb-0.5">Execution</strong>
                      <p className="text-[10px] text-slate-500 leading-tight">End-to-end supply chain mastery from origin to vessel discharge.</p>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <Globe className="w-5 h-5 text-[#2C5282] mb-1.5" />
                      <strong className="text-xs text-[#002046] block mb-0.5">Global Scale</strong>
                      <p className="text-[10px] text-slate-500 leading-tight">Direct connectivity with major Mediterranean &amp; Red Sea maritime hubs.</p>
                    </div>
                  </div>
                </div>

                {/* Verbatim Message from the CEO */}
                <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl relative">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <Quote className="w-5 h-5 text-[#2C5282]" />
                      <h3 className="font-display font-bold text-base text-[#002046]">Message from the CEO</h3>
                    </div>
                    <span className="text-[9px] font-technical uppercase tracking-wider text-slate-400 font-semibold">Executive Office</span>
                  </div>

                  <div className="space-y-3 text-xs text-slate-700 leading-relaxed italic mb-5">
                    <p>
                      "At Awtad Trade, we take pride in being a trusted international trade partner, delivering high-quality strategic commodities, industrial materials, and agricultural products across global markets."
                    </p>
                    <p>
                      "Our foundation is built on transparency, uncompromised quality, and reliable execution. We are dedicated to offering integrated supply solutions that meet the evolving demands of our clients and support growth across international markets."
                    </p>
                    <p>
                      "We believe in long-term strategic growth and strive to cultivate strong, mutually beneficial partnerships with our global clients and suppliers."
                    </p>
                  </div>

                  <div className="flex items-end justify-between pt-3 border-t border-slate-200/80">
                    <div>
                      <span className="text-[11px] font-serif italic text-slate-500 block">Sincerely,</span>
                      <h4 className="font-display font-bold text-sm text-[#002046]">Dr. Salma Salah</h4>
                      <p className="text-[10px] font-technical uppercase text-[#2C5282] font-semibold">
                        Chief Executive Officer &ndash; Awtad Trade
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-1 rounded border border-slate-200 h-10 w-20 flex items-center justify-center">
                        <img src={LOGO_URL} alt="Seal" className="h-full w-auto object-contain mix-blend-multiply brightness-125 contrast-125" />
                      </div>
                      <div className="text-right text-[9px] font-technical text-slate-500">
                        <span className="block font-semibold text-[#002046]">Headquarters</span>
                        <span>Cairo, Egypt</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Corporate Address Details */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-700">
                    <MapPin className="w-4 h-4 text-[#2C5282]" />
                    <span className="font-technical font-semibold">4 Makram Ebeid Street, Nasr City - Cairo - Egypt</span>
                  </div>
                  <span className="text-[10px] font-technical text-slate-500 uppercase tracking-wider">Commercial Registry &bull; Cairo, Egypt</span>
                </div>
              </div>

              <PDF_PageFooter pageNumber={2} totalPages={8} title="Corporate Overview & Executive Statement" />
            </section>

            {/* PAGE 3: INDUSTRIAL SULFUR (GRANULAR & MICRONIZED) */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Elemental Sulfur Commodities" category="Industrial & Agricultural Sulfur" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden gap-5">
                {/* Product 1: Granular Sulfur */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={GRANULAR_IMG} alt="Granular Sulfur" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Granular Sulfur</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-amber-100 text-amber-800">
                              Purity &ge;99.9%
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Chemical Formula: S (CAS #7704-34-9) &bull; Hydro-desulfurization origin
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        High Analysis
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic &amp; Industrial Use: </strong>
                        Crucial secondary macronutrient essential for nitrogen fixation, amino acid synthesis (cysteine, methionine), and chlorophyll formation in crops. Extensively used in alkaline soils to lower pH, manufactured into sulfur-bentonite pastilles, and serves as primary feedstock for industrial sulfuric acid production.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Granulation: </strong>
                        Recovered from natural gas sweetening and hydro-desulfurization of crude oil at world-scale refineries. Molten sulfur is treated, degassed, and formed into spherical solid granules (2.0-5.0mm) via fluid bed air/water granulation with dust suppressant coating.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Purity (Dry Basis)</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 99.9% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Granule Sizing</span>
                        <strong className="text-slate-800 text-[10px]">2.0 &ndash; 5.0 mm (&ge;90%)</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Ash Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 0.03% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Moisture Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 0.5% Max</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product 2: Micronized Sulfur */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={MICRONIZED_IMG} alt="Micronized Sulfur" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Micronized Sulfur Powder</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-amber-100 text-amber-800">
                              100 &ndash; 325 Mesh
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Ultra-Fine Particle Chemistry &bull; Contact Fungicide &amp; Polymer Vulcanization
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Specialty Grade
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Crop Protection &amp; Fungicidal Uses: </strong>
                        High-efficiency contact fungicide and acaricide widely sprayed across vineyards, orchards, and horticulture to suppress powdery mildew and mite infestations without leaving toxic systemic residues.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Rubber Vulcanization &amp; Polymers: </strong>
                        Finely air-classified sulfur particles cross-link polyisoprene polymer chains during tire manufacture, providing thermal resistance, elastic memory, and mechanical durability in technical rubber goods.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Purity (S)</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 99.8% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Fineness Passing</span>
                        <strong className="text-slate-800 text-[10px]">325 Mesh (&ge;99.5%)</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Acidity (as H₂SO₄)</span>
                        <strong className="text-slate-800 text-[10px]">&le; 0.02% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Conditioning</span>
                        <strong className="text-slate-800 text-[10px]">Free-Flowing / Anti-Cake</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={3} totalPages={8} title="Industrial Sulfur & Micronized Derivatives" />
            </section>

            {/* PAGE 4: PHOSPHATE COMMODITIES (PHOSPHATE ROCK & SSP) */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Phosphate Minerals & Superphosphates" category="Phosphorus Nutrition" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden gap-5">
                {/* Product 3: Phosphate Rock */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={PHOSPHATE_ROCK_IMG} alt="Phosphate Rock" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Phosphate Rock (PR)</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-emerald-100 text-emerald-800">
                              28% &ndash; 34% P₂O₅
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: Ca₁₀(PO₄)₆(F,Cl,OH)₂ [Apatite Mineral] &bull; Sedimentary Marine Origin
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Raw Feedstock
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        Phosphorus additions are needed globally to improve soil fertility and crop production. Unprocessed phosphate rock (PR) applied directly to soil can provide a valuable plant nutrient source under specific conditions&mdash;especially in acidic soils, agroforestry, and organic farming systems, providing sustained long-term phosphorus release.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Extraction: </strong>
                        Obtained from geologic deposits worldwide (primarily apatite, a calcium phosphate mineral). Extracted mainly from sedimentary marine deposits through surface mining, screened near the mine site to remove impurities. Also serves as primary precursor for phosphoric acid, SSP, and TSP manufacturing.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">P₂O₅ Grade</span>
                        <strong className="text-slate-800 text-[10px]">28.0% &ndash; 34.0%</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">BPL Content</span>
                        <strong className="text-slate-800 text-[10px]">68% &ndash; 74% BPL</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Moisture Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 3.0% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Grain Sizing</span>
                        <strong className="text-slate-800 text-[10px]">0 &ndash; 2 mm Screened</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product 4: Single Superphosphate (SSP) */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={SSP_IMG} alt="Single Superphosphate" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Single Superphosphate (SSP)</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-emerald-100 text-emerald-800">
                              16% &ndash; 20% P₂O₅ + 12% S
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: Ca(H₂PO₄)₂ + 2CaSO₄ &bull; Built-in Gypsum &amp; Sulfur
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Dual Nutrient
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        Single Superphosphate is an effective, phosphorus-rich fertilizer ideal for crops requiring high amounts of phosphorus, such as fruits, vegetables, and flowers. Enriches root architecture, flowering, and seed development while simultaneously supplying vital sulfur and calcium.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Reaction Equation: </strong>
                        Ground phosphate rock reacts with sulfuric acid to form a semi-solid that cures in a den and storage pile before being milled and granulated.
                      </p>
                      <div className="p-2 bg-white rounded border border-slate-200 font-mono text-[9px] text-[#002046] text-center">
                        Ca₃(PO₄)₂ [rock phosphate] + 2H₂SO₄ [sulfuric acid] &rarr; Ca(H₂PO₄)₂ [monocalcium phosphate] + 2CaSO₄ [gypsum]
                      </div>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Available P₂O₅</span>
                        <strong className="text-slate-800 text-[10px]">16.0% &ndash; 20.0%</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Sulfur (S)</span>
                        <strong className="text-slate-800 text-[10px]">11.0% &ndash; 12.0%</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Calcium (Ca)</span>
                        <strong className="text-slate-800 text-[10px]">19.0% &ndash; 21.0%</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Physical Form</span>
                        <strong className="text-slate-800 text-[10px]">Granular 2.0-4.5mm</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={4} totalPages={8} title="Phosphate Rock & Single Superphosphate" />
            </section>

            {/* PAGE 5: CONCENTRATED PHOSPHATES & NITROGEN (TSP & UREA) */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="High-Analysis Commodities" category="Concentrated Fertilizers" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden gap-5">
                {/* Product 5: Triple Superphosphate (TSP) */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={TSP_IMG} alt="Triple Superphosphate" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Triple Superphosphate (TSP)</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-emerald-100 text-emerald-800">
                              46% P₂O₅ High Density
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: Ca(H₂PO₄)₂·H₂O (Monocalcium Phosphate) &bull; Maximum Water Solubility
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        High Density
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        Widely used in agriculture to promote robust root growth, early tillering, and higher yields in crops requiring substantial phosphorus levels (such as fruits, vegetables, legumes, and flowers). Its high P concentration minimizes ocean freight costs per unit of nutrient delivered.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Granulation: </strong>
                        Produced by reacting finely ground phosphate rock with liquid phosphoric acid in a cone mixer. Granular TSP is made by spraying slurry as a coating onto small seed particles, followed by rotary drying and curing to guarantee high mechanical hardness and zero dusting.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Total P₂O₅</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 46.0% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Water Soluble P₂O₅</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 38.0% (&gt;85% of total)</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Free Acid (as P₂O₅)</span>
                        <strong className="text-slate-800 text-[10px]">&le; 4.5% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Granule Size</span>
                        <strong className="text-slate-800 text-[10px]">2.0 &ndash; 4.5 mm (&ge;90%)</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product 6: Urea 46% */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={UREA_IMG} alt="Urea 46%" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Urea (Nitrogen 46%)</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-blue-100 text-blue-800">
                              46% N Solid Nitrogen
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: CO(NH₂)₂ [CAS #57-13-6] &bull; Prilled &amp; Granular Grades
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Global Preeminent
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        Urea is the most widely used solid nitrogen fertilizer globally, featuring a high N content that makes it efficient to transport and apply to crops requiring large amounts of nitrogen, such as corn, wheat, and rice. Highly soluble for broadcast application and fertigation.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Logistics: </strong>
                        Produced through the controlled reaction of ammonia gas (NH₃) and carbon dioxide (CO₂) at elevated temperatures and pressures. Formed into spherical granules or hardened prills with low biuret content (&le;1.0%). Dispatched via ocean vessel, barge, rail, and containerized jumbo bags.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Total Nitrogen</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 46.0% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Biuret Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 1.0% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Moisture Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 0.5% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Form</span>
                        <strong className="text-slate-800 text-[10px]">Granular (2-4mm) / Prilled</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={5} totalPages={8} title="Triple Superphosphate & Urea 46%" />
            </section>

            {/* PAGE 6: COMPOUND & NITROGEN-SULFUR (NPK & AMMONIUM SULFATE) */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Compound & Multi-Nutrients" category="Agricultural Chemistry" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden gap-5">
                {/* Product 7: NPK Compound */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={NPK_IMG} alt="NPK Compound" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">NPK Compound Fertilizers</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-indigo-100 text-indigo-800">
                              Custom Multi-Ratio Blends
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: N - P₂O₅ - K₂O Complex &bull; Field Crops, Orchards &amp; Hydroponics
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Balanced Yield
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        NPK is a compound fertilizer containing different ratios of nitrogen (N), phosphorus (P), and potassium (K). Used across field crops, hydroponics, and container gardening to boost plant productivity, quality, disease resistance, and environmental tolerance.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Manufacturing &amp; Conditioning: </strong>
                        Manufactured via ammoniation-granulation or pipe-reactor melt agglomeration combining ammonium phosphate, urea, and potassium chloride or sulfate. Granules are polymer-coated to prevent caking during maritime transport.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Formulations</span>
                        <strong className="text-slate-800 text-[10px]">15-15-15, 20-20-20, 12-12-17</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Granule Size</span>
                        <strong className="text-slate-800 text-[10px]">2.0 &ndash; 4.75 mm (&ge;90%)</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Nutrient Ratio</span>
                        <strong className="text-slate-800 text-[10px]">Homogeneous Pellet</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Conditioning</span>
                        <strong className="text-slate-800 text-[10px]">Anti-Caking Coated</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product 8: Ammonium Sulfate */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 shrink-0">
                          <img src={AMMONIUM_SULFATE_IMG} alt="Ammonium Sulfate" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-base text-[#002046]">Ammonium Sulfate</h3>
                            <span className="px-2 py-0.5 rounded text-[9px] font-technical font-bold bg-blue-100 text-blue-800">
                              21% N + 24% S
                            </span>
                          </div>
                          <span className="text-[10px] font-technical text-slate-500 block">
                            Formula: (NH₄)₂SO₄ [CAS #7783-20-2] &bull; 100% Water Soluble Crystals &amp; Granules
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] font-technical font-semibold text-[#2C5282] uppercase tracking-wider bg-blue-50 px-2.5 py-1 rounded">
                        Dual Nutrition
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-slate-600 mt-2 mb-3">
                      <p>
                        <strong className="text-[#002046]">Agronomic Use: </strong>
                        Versatile fertilizer commonly used to supply nitrogen and sulfur. Especially valuable for crops requiring high nitrogen levels, such as wheat, corn, and barley, and its high solubility suits diverse agricultural applications including soil broadcasting and fertigation.
                      </p>
                      <p>
                        <strong className="text-[#002046]">Production &amp; Crystal Quality: </strong>
                        Manufactured by reacting sulfuric acid with heated ammonia gas. Crystals are dried, screened, and coated with a conditioner. Highly resistant to nitrogen volatilization loss compared to urea in alkaline soils.
                      </p>
                    </div>
                  </div>

                  {/* Spec table */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden text-[10px]">
                    <div className="bg-slate-100/80 px-3 py-1.5 font-technical font-bold text-[#002046] uppercase tracking-wider border-b border-slate-200">
                      Technical Specification Parameters
                    </div>
                    <div className="grid grid-cols-4 divide-x divide-slate-200 p-2 text-center">
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Nitrogen (Ammoniacal)</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 21.0% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Sulfur (Sulfate)</span>
                        <strong className="text-slate-800 text-[10px]">&ge; 24.0% Min</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Moisture Content</span>
                        <strong className="text-slate-800 text-[10px]">&le; 0.5% Max</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px] font-technical uppercase">Appearance</span>
                        <strong className="text-slate-800 text-[10px]">Crystalline / Granular</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={6} totalPages={8} title="NPK Compounds & Ammonium Sulfate" />
            </section>

            {/* PAGE 7: SUPPLY CHAIN, TESTING & INDUSTRIAL APPLICATIONS */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Logistics & Industrial Applications" category="Operations & Quality" />

              <div className="flex-1 px-12 py-6 flex flex-col justify-between overflow-hidden gap-5">
                {/* Logistics Infrastructure */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-[#2C5282]" />
                    <h3 className="font-display font-bold text-sm text-[#002046]">Global Maritime &amp; Terminal Logistics</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <strong className="text-[11px] text-[#002046] block mb-1">Deep-Water Terminals</strong>
                      <p className="text-[10px] text-slate-500 leading-normal">
                        Commanding large-capacity terminal stockpiles inside major Mediterranean, Red Sea, and Middle Eastern shipping hubs.
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <strong className="text-[11px] text-[#002046] block mb-1">Vessel Chartering</strong>
                      <p className="text-[10px] text-slate-500 leading-normal">
                        Chartering bulk carriers (Handysize, Supramax, Panamax) with full ocean bills of lading and strict laytime monitoring.
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <strong className="text-[11px] text-[#002046] block mb-1">Multimodal Packing</strong>
                      <p className="text-[10px] text-slate-500 leading-normal">
                        Bulk loose vessel loading, 1000kg-1200kg UV-stabilized heavy duty jumbo bags, and containerized break-bulk.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quality Assurance */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[#2C5282]" />
                    <h3 className="font-display font-bold text-sm text-[#002046]">Quality Assurance &amp; Independent Laboratory Inspection</h3>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between gap-4">
                    <div className="flex-1 text-xs text-slate-600 leading-relaxed">
                      Every cargo is strictly sampled and certified by premier independent inspection agencies (<strong>SGS, Intertek, Bureau Veritas</strong>). Shipments include complete Certificates of Analysis (CoA) validating chemical purity, moisture thresholds, and granulometric distribution under ASTM D4533 / ISO protocols.
                    </div>
                    <div className="bg-white p-2 rounded border border-slate-200 flex flex-col items-center justify-center shrink-0 w-32 text-center">
                      <Award className="w-6 h-6 text-[#2C5282] mb-1" />
                      <span className="text-[8px] font-technical uppercase font-bold text-[#002046]">ASTM Certified</span>
                      <span className="text-[7px] text-slate-400 font-technical">ISO Conformity</span>
                    </div>
                  </div>
                </div>

                {/* Industrial Applications Matrix */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-[#2C5282]" />
                    <h3 className="font-display font-bold text-sm text-[#002046]">Industrial &amp; Strategic Applications Matrix</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">🌾</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Agriculture &amp; Fertilizers</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Feedstock for phosphate fertilizers, SSP, TSP, NPK blends, and soil conditioning.</p>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">🧪</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Chemical Manufacturing</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Precursor for sulfuric acid, detergents, pigments, and chemical reagents.</p>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">🚗</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Rubber &amp; Polymers</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Vulcanizing agent in automotive tires and high-grade synthetic elastomers.</p>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">💊</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Pharmaceuticals</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Active additive in antimicrobial and dermatological formulations.</p>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">🔋</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Energy Storage &amp; Tech</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Advanced cathode materials for next-generation lithium-sulfur battery cells.</p>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <span className="text-base mb-1 block">🏗️</span>
                      <strong className="text-[11px] text-[#002046] block mb-0.5">Construction Polymers</strong>
                      <p className="text-[9px] text-slate-500 leading-normal">Structural binders for high-durability sulfur concrete and marine asphalt.</p>
                    </div>
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={7} totalPages={8} title="Supply Chain, Quality Standards & Industry Uses" />
            </section>

            {/* PAGE 8: CORPORATE DIRECTORY & TRADE DESK */}
            <section 
              className="a4-page relative overflow-hidden bg-white flex flex-col justify-between" 
              style={{ width: '794px', height: '1123px', minHeight: '1123px', maxHeight: '1123px', boxSizing: 'border-box' }}
            >
              <PDF_PageHeader sectionTitle="Corporate Directory & Trade Desk" category="Commercial Inquiries" />

              <div className="flex-1 px-12 py-8 flex flex-col justify-between overflow-hidden">
                {/* Top corporate entity block */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-1 bg-[#F2C94C]"></div>
                    <h2 className="font-display text-2xl font-bold text-[#002046]">Official Commercial Trade Desk</h2>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-6">
                    Awtad Trade operates dedicated international commodity trading desks for raw materials procurement, chartered vessel logistics, and custom fertilizer formulations. All trade interactions are conducted under strict international trade compliance and commercial governance.
                  </p>

                  {/* Headquarters details box */}
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl mb-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <span className="text-[9px] font-technical uppercase tracking-widest text-[#2C5282] font-bold block mb-1">
                          Headquarters Address
                        </span>
                        <h4 className="font-display font-bold text-sm text-[#002046] mb-1">
                          4 Makram Ebeid Street, Nasr City - Cairo - Egypt
                        </h4>
                        <p className="text-[10px] text-slate-500 font-technical">
                          Corporate Office &bull; Cairo Commercial Trade Registry
                        </p>
                      </div>

                      <div>
                        <span className="text-[9px] font-technical uppercase tracking-widest text-[#2C5282] font-bold block mb-1">
                          Official Direct Communication
                        </span>
                        <div className="text-sm font-semibold text-[#002046] mb-1 font-technical">
                          info@awtadtrade.com
                        </div>
                        <div className="text-[10px] text-slate-500 font-technical">
                          Web: www.awtadtrade.com
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Procurement Guidelines */}
                  <div className="p-6 bg-white border border-slate-200 rounded-xl mb-6">
                    <h3 className="font-display font-bold text-sm text-[#002046] mb-3">
                      Procurement &amp; Cargo Inquiries Guidelines
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-xs text-slate-600">
                      <div>
                        <strong className="text-[#002046] block text-[11px] mb-1">Incoterms &amp; Delivery</strong>
                        <p className="text-[10px] leading-relaxed">
                          Supported terms include FOB (Port of Loading), CIF (Destination Port), and CFR. Direct oceanic vessel chartering and containerized freight.
                        </p>
                      </div>
                      <div>
                        <strong className="text-[#002046] block text-[11px] mb-1">Order Consignments</strong>
                        <p className="text-[10px] leading-relaxed">
                          Minimum order quantities from full container loads (FCL 25-28 MT) to chartered vessel bulk consignments (5,000 MT &ndash; 50,000 MT).
                        </p>
                      </div>
                      <div>
                        <strong className="text-[#002046] block text-[11px] mb-1">Standard Documentation Package</strong>
                        <p className="text-[10px] leading-relaxed">
                          Full set Clean on Board Ocean Bills of Lading, Commercial Invoice, Packing List, Certificate of Origin, and Certificate of Analysis (CoA).
                        </p>
                      </div>
                      <div>
                        <strong className="text-[#002046] block text-[11px] mb-1">Independent Inspection</strong>
                        <p className="text-[10px] leading-relaxed">
                          Mandatory third-party inspection certificates issued by SGS, Intertek, or Bureau Veritas at load port.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Executive Seal Sign-off Card */}
                <div className="p-6 bg-[#002046] text-white rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-[9px] font-technical uppercase tracking-widest text-[#F2C94C] font-bold block mb-1">
                      Institutional Certification
                    </span>
                    <h4 className="font-display font-bold text-base text-white mb-0.5">
                      Awtad Trade &bull; Global Commodities
                    </h4>
                    <p className="text-[10px] text-white/70 font-technical">
                      4 Makram Ebeid Street, Nasr City - Cairo - Egypt
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded-lg flex items-center justify-center h-12 w-28 shrink-0">
                    <img src={LOGO_URL} alt="Awtad Trade Seal" className="max-h-full max-w-full object-contain mix-blend-multiply brightness-125 contrast-125" />
                  </div>
                </div>
              </div>

              <PDF_PageFooter pageNumber={8} totalPages={8} title="Corporate Directory & Commercial Desk" />
            </section>

          </main>
        </div>
      )}

    </div>
  );
}
